import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { Search, Info, DollarSign } from 'lucide-react';

const Pricing = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [tooltip, setTooltip] = useState({ show: false, content: '', type: '', position: { top: 0, left: 0 } });
  const tooltipRef = useRef(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [searchTerm, products]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, { withCredentials: true });
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      toast.error('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    if (!searchTerm.trim()) {
      setFilteredProducts(products);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = products.map(category => {
      const categoryMatches = category.category.toLowerCase().includes(term);
      const filteredServices = category.services.filter(service =>
        service.name.toLowerCase().includes(term)
      );

      if (categoryMatches || filteredServices.length > 0) {
        return {
          ...category,
          services: categoryMatches ? category.services : filteredServices
        };
      }
      return null;
    }).filter(Boolean);

    setFilteredProducts(filtered);
  };

  const showTooltip = (e, content, type) => {
    const button = e.currentTarget;
    const rect = button.getBoundingClientRect();
    
    setTooltip({
      show: true,
      content,
      type,
      position: {
        top: rect.top + window.scrollY - 10,
        left: rect.left + rect.width / 2
      }
    });
  };

  const hideTooltip = () => {
    setTooltip({ show: false, content: '', type: '', position: { top: 0, left: 0 } });
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <header style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '24px' }}>
          Installation Pricing
        </h1>
        
        <div style={{ maxWidth: '600px', margin: '0 auto', position: 'relative' }}>
          <Search 
            size={20} 
            style={{ 
              position: 'absolute', 
              left: '16px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: '#626C71'
            }} 
          />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search services and categories..."
            style={{
              width: '100%',
              padding: '16px 20px 16px 48px',
              fontSize: '17px',
              border: '2px solid rgba(0, 0, 0, 0.1)',
              borderRadius: '12px',
              outline: 'none',
              transition: 'all 0.2s ease'
            }}
            onFocus={(e) => e.target.style.borderColor = '#FDB515'}
            onBlur={(e) => e.target.style.borderColor = 'rgba(0, 0, 0, 0.1)'}
          />
        </div>
      </header>

      <main>
        {filteredProducts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#626C71' }}>
            <Search size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
            <p style={{ fontSize: '17px' }}>No services found matching your search.</p>
          </div>
        ) : (
          filteredProducts.map((category, idx) => (
            <div
              key={idx}
              style={{
                background: 'white',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                borderRadius: '12px',
                marginBottom: '32px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                overflow: 'hidden'
              }}
            >
              <div
                style={{
                  background: '#FDB515',
                  color: 'white',
                  padding: '20px 24px',
                  fontSize: '24px',
                  fontWeight: '700'
                }}
              >
                {category.category}
              </div>

              <div style={{ padding: '16px' }}>
                {category.services.map((service, serviceIdx) => (
                  <div
                    key={serviceIdx}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '16px',
                      borderBottom: serviceIdx < category.services.length - 1 ? '1px solid rgba(0, 0, 0, 0.1)' : 'none',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(253, 181, 21, 0.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                      <div style={{ fontSize: '15px', fontWeight: '500' }}>
                        {service.name}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {service.notes && (
                          <button
                            onMouseEnter={(e) => showTooltip(e, service.notes, 'info')}
                            onMouseLeave={hideTooltip}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '8px',
                              borderRadius: '50%',
                              color: '#626C71',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.color = '#FDB515';
                              e.currentTarget.style.backgroundColor = 'rgba(253, 181, 21, 0.1)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.color = '#626C71';
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <Info size={16} />
                          </button>
                        )}
                        <button
                          onMouseEnter={(e) => showTooltip(e, service.spiff, 'spiff')}
                          onMouseLeave={hideTooltip}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '8px',
                            borderRadius: '50%',
                            color: '#626C71',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.color = '#FDB515';
                            e.currentTarget.style.backgroundColor = 'rgba(253, 181, 21, 0.1)';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.color = '#626C71';
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <DollarSign size={16} />
                        </button>
                      </div>
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#FDB515', marginLeft: '16px' }}>
                      ${service.price}
                    </div>
                  </div>
                ))}
              </div>

              {category.materials && (
                <div
                  style={{
                    background: 'rgba(253, 181, 21, 0.1)',
                    border: '1px solid rgba(253, 181, 21, 0.3)',
                    borderRadius: '8px',
                    padding: '16px',
                    margin: '16px'
                  }}
                >
                  <div style={{ fontWeight: '700', color: '#e5a314', marginBottom: '8px', fontSize: '15px' }}>
                    Materials Included
                  </div>
                  <div style={{ fontSize: '14px', color: '#1f2121' }}>
                    {category.materials}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </main>

      {/* Tooltip */}
      {tooltip.show && (
        <div
          ref={tooltipRef}
          style={{
            position: 'absolute',
            top: `${tooltip.position.top}px`,
            left: `${tooltip.position.left}px`,
            transform: 'translate(-50%, -100%)',
            background: 'rgba(31, 33, 33, 0.95)',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            lineHeight: '1.5',
            maxWidth: '320px',
            zIndex: 1000,
            pointerEvents: 'none',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
          }}
        >
          {tooltip.type === 'spiff' ? (
            <>
              <div style={{ fontWeight: '700', fontSize: '13px', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Spiff Amount
              </div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#FDB515' }}>
                ${tooltip.content}
              </div>
            </>
          ) : (
            tooltip.content
          )}
        </div>
      )}
    </div>
  );
};

export default Pricing;
